import { Component, OnDestroy, OnInit, TemplateRef, ViewChild, signal } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { mainConfig } from '@config/main.config';
import { Categoria } from '@models/categoria.model';
import { CategorieService } from '@services/categorie.service';
import { UtilsService } from '@services/utils.service';
import { PageRequest } from '@sinapsys/ngx-crud';
import { ConfermaService } from '@shared/conferma/conferma-dialog.component';
import { ColumnDefinition } from '@shared/data-table/column-definition';
import { DataTableComponent } from '@shared/data-table/data-table.component';
import { Subscription, debounceTime, distinctUntilChanged } from 'rxjs';
import { PageComponent } from '../../../layout/page/page.component';

@Component({
  selector: 'app-categorie',
  standalone: true,
  imports: [DataTableComponent, PageComponent, mainConfig.material, mainConfig.formModules],
  templateUrl: './categorie.component.html',
})
export class CategorieComponent implements OnInit, OnDestroy {
  @ViewChild(DataTableComponent, { static: false }) table!: DataTableComponent<Categoria>;
  @ViewChild('detailsModal', { static: true }) detailsModal!: TemplateRef<unknown>;
  @ViewChild('rubricaCella', { static: true }) rubricaCella!: TemplateRef<unknown>;
  @ViewChild('azioniCella', { static: true }) azioniCella!: TemplateRef<unknown>;

  columnDefinitions: ColumnDefinition<Categoria>[] = [];
  searchSubscription?: Subscription;

  readonly filtroNome = new FormControl<string>('', { nonNullable: true });
  readonly filtroRubrica = new FormControl<string | null>(null);

  readonly caricamento = signal(false);
  readonly errore = signal<string | null>(null);
  readonly totale = signal<number | null>(null);

  editItemForm: FormGroup;
  editItem?: Categoria;
  dialogRef?: MatDialogRef<unknown>;

  private readonly sottoscrizioni = new Subscription();

  constructor(
    public utilsService: UtilsService,
    private categorieService: CategorieService,
    private dialog: MatDialog,
    private conferma: ConfermaService
  ) {
    this.editItemForm = new FormGroup({
      id: new FormControl<number | undefined>(undefined),
      nome: new FormControl<string | undefined>(undefined),
      permalink: new FormControl<string | undefined>(undefined),
      rubrica: new FormControl<string | undefined>(undefined),
    });
  }

  ngOnDestroy(): void {
    this.sottoscrizioni.unsubscribe();
  }

  ngOnInit(): void {
    this.columnDefinitions = [
      { id: 'nome', header: 'Nome', sortable: true },
      { id: 'permalink', header: 'Permalink', sortable: true, nascondiSuMobile: true },
      { id: 'rubrica', header: 'Rubrica', sortable: true, template: this.rubricaCella, width: '120px' },
      { id: 'id', header: 'ID', sortable: true, width: '90px', nascondiSuMobile: true },
      { id: '_', header: '', template: this.azioniCella, width: '56px', allineamento: 'fine' },
    ];

    this.sottoscrizioni.add(
      this.filtroNome.valueChanges.pipe(debounceTime(350), distinctUntilChanged()).subscribe(() => this.search())
    );
    this.sottoscrizioni.add(this.filtroRubrica.valueChanges.subscribe(() => this.search()));
  }

  search(mantieniPagina?: boolean) {
    if (!this.table) return;

    if (!this.table.sort.active) {
      this.table.sort.active = 'nome';
      this.table.sort.direction = 'asc';
    }
    if (!mantieniPagina) this.table.paginator.pageIndex = 0;

    const q: Record<string, unknown> = {};
    const nome = this.filtroNome.value.trim();
    if (nome) q['nome'] = { $like: nome };
    if (this.filtroRubrica.value) q['rubrica'] = this.filtroRubrica.value;

    const pageRequest = new PageRequest(
      this.table.paginator.pageIndex,
      this.table.paginator.pageSize,
      this.table.sort.direction,
      this.table.sort.active
    );

    this.searchSubscription?.unsubscribe();
    this.caricamento.set(true);
    this.errore.set(null);

    this.searchSubscription = this.categorieService.search(q, pageRequest).subscribe({
      next: (res) => {
        res.items = res.items.map((item) => new Categoria(item));
        this.table.populate(res);
        this.totale.set(res.total);
        this.caricamento.set(false);
      },
      error: (err) => {
        this.caricamento.set(false);
        this.totale.set(null);
        this.errore.set(err?.error?.message || err?.error?.messaggio || err?.message || 'Errore inatteso');
        this.utilsService.defaultHttpErrorHandler(err);
      },
    });
  }

  filtriAttivi(): boolean {
    return !!this.filtroNome.value.trim() || !!this.filtroRubrica.value;
  }

  azzeraFiltri(): void {
    this.filtroNome.setValue('', { emitEvent: false });
    this.filtroRubrica.setValue(null, { emitEvent: false });
    this.search();
  }

  sottotitolo(): string {
    const totale = this.totale();
    if (totale === null) return '';
    if (totale === 0) return this.filtriAttivi() ? 'Nessuna categoria con questi filtri' : 'Nessuna categoria';
    return totale === 1 ? '1 categoria' : `${totale.toLocaleString('it-IT')} categorie`;
  }

  new() {
    this.edit(new Categoria());
  }

  edit(item: Categoria) {
    this.editItemForm.reset();
    this.editItemForm.patchValue(item);
    this.editItem = item;
    this.dialogRef = this.dialog.open(this.detailsModal, { width: '520px' });
  }

  save() {
    const raw = this.editItemForm.getRawValue();
    this.categorieService.save(raw).subscribe({
      next: () => {
        this.utilsService.notificationsService.showNotification('', 'Categoria salvata', 'success');
        this.search(true);
        this.dialogRef?.close();
      },
      error: (err) => this.utilsService.defaultHttpErrorHandler(err),
    });
  }

  async elimina() {
    const nome = this.editItemForm.controls['nome'].value ?? '';
    const procedi = await this.conferma.chiedi({
      titolo: 'Eliminare questa categoria?',
      testo: `"${nome}" verrà rimossa. Gli articoli che la usano resteranno senza categoria.`,
      conferma: 'Elimina',
      distruttiva: true,
    });
    if (!procedi) return;

    this.categorieService.delete(this.editItemForm.controls['id'].value).subscribe({
      next: () => {
        this.utilsService.notificationsService.showNotification('', 'Categoria eliminata', 'success');
        this.search(true);
        this.dialogRef?.close();
      },
      error: (err) => this.utilsService.defaultHttpErrorHandler(err),
    });
  }
}
